import { Skeleton } from 'antd';
import { makeAutoObservable } from 'mobx';
import { lazy, type ReactNode } from 'react';

import { getDocumentsByPatientIdOnce } from '../../server/PatientPortraitApi/PatientPortraitApi';
import { EntitiesFetchingStatus } from '../../utilities/apiCommunication';

import type { NID } from '@navina/api-types';
import type { RouteObject } from 'react-router';

export enum PageTab {
	DxSummary = 'DxSummary',
	DocumentsList = 'DocumentsList',
	ClinicalSummary = 'ClinicalSummary',
	Quality = 'Quality',
	Ambient = 'Ambient',
}

const BulbIcon = lazy(() => import('./icons/Bulb.svg').then((module) => ({ default: module.ReactComponent })));
const DocumentIcon = lazy(() => import('./icons/Document.svg').then((module) => ({ default: module.ReactComponent })));
const ShiningDiamondIcon = lazy(() =>
	import('./icons/ShiningDiamond.svg').then((module) => ({ default: module.ReactComponent })),
);
const StethoscopeIcon = lazy(() =>
	import('./icons/Stethoscope.svg').then((module) => ({ default: module.ReactComponent })),
);
const MicrophoneIcon = lazy(() =>
	import('./icons/Microphone.svg').then((module) => ({ default: module.ReactComponent })),
);

const DxSummaryPage = lazy(() =>
	import('../PatientDiagnosisSummaryPage/PatientDiagnosisSummaryPage').then((module) => ({
		default: module.PatientDiagnosisSummaryPage,
	})),
);
const DocumentsListAdapter = lazy(() =>
	import('../ClinicalDocumentsPage/ClinicalDocumentsPage').then((module) => ({
		default: module.ClinicalDocumentsPage,
	})),
);
const ClinicalSummaryPage = lazy(() =>
	import('../ClinicalSummaryPage/ClinicalSummaryPage').then((module) => ({
		default: module.ClinicalSummaryPage,
	})),
);
const QualityTabPage = lazy(() =>
	import('../QualityTabPage/QualityTabPage').then((module) => ({ default: module.QualityTabPage })),
);

const AmbientTabPage = lazy(() =>
	import('../AmbientTabPage/AmbientTabPage').then((module) => ({ default: module.AmbientTabPage })),
);

interface Tab extends Readonly<Required<Pick<RouteObject, 'path' | 'element'>>> {
	readonly tab: PageTab;
	readonly title: ReactNode;
	readonly shortTitle: ReactNode;
	readonly name: string;
	readonly featureFlagName: string;
	readonly icon: ReactNode;
	readonly showSideMenu: boolean;
	readonly initialize?: (patientId: NID) => Promise<void>;
}

interface DocumentListTabParams {
	readonly getDocumentsAmount: (patientId: NID) => Promise<number>;
}
class DocumentListTabModel implements Tab {
	private readonly getDocumentsAmount: (patientId: NID) => Promise<number>;
	private maybeDocumentsAmount: number | null = null;
	private documentsAmountFetchingStatus: EntitiesFetchingStatus = EntitiesFetchingStatus.Idle;
	private constructor(params: DocumentListTabParams) {
		this.getDocumentsAmount = params.getDocumentsAmount;
		makeAutoObservable(this);
	}
	static readonly create = (params: DocumentListTabParams) => new DocumentListTabModel(params);

	private readonly setDocumentsAmountFetchingStatus = (status: EntitiesFetchingStatus) => {
		this.documentsAmountFetchingStatus = status;
	};

	private readonly setDocumentsAmount = (amount: number | null) => {
		this.maybeDocumentsAmount = amount;
	};

	private get documentsAmount(): NonNullable<typeof this.maybeDocumentsAmount> {
		const { maybeDocumentsAmount } = this;
		if (maybeDocumentsAmount === null) {
			throw new Error('Documents amount is not defined, please invoke fetchDocumentsAmount method first');
		}
		return maybeDocumentsAmount;
	}

	readonly initialize = async (patientId: NID): Promise<void> => {
		const { fetchDocumentsAmount } = this;
		await fetchDocumentsAmount(patientId);
	};

	readonly fetchDocumentsAmount = async (patientId: NID): Promise<void> => {
		const { setDocumentsAmountFetchingStatus, setDocumentsAmount: setMaybeDocumentsAmount, getDocumentsAmount } = this;
		try {
			setDocumentsAmountFetchingStatus(EntitiesFetchingStatus.Pending);
			const amount = await getDocumentsAmount(patientId);
			setMaybeDocumentsAmount(amount);
			setDocumentsAmountFetchingStatus(EntitiesFetchingStatus.Success);
		} catch {
			setDocumentsAmountFetchingStatus(EntitiesFetchingStatus.Error);
		}
	};

	get documentsAmountOrSkeleton(): ReactNode {
		const { documentsAmountFetchingStatus } = this;

		if (
			documentsAmountFetchingStatus === EntitiesFetchingStatus.Idle ||
			documentsAmountFetchingStatus === EntitiesFetchingStatus.Error
		) {
			return null;
		}

		if (documentsAmountFetchingStatus === EntitiesFetchingStatus.Pending) {
			return (
				<Skeleton.Button
					{...{
						active: true,
						size: 'small',
						shape: 'round',
						style: { zoom: 0.7, maxInlineSize: '20px' },
					}}
				/>
			);
		}

		return this.documentsAmount;
	}

	get shortTitle() {
		const { documentsAmountOrSkeleton } = this;

		if (documentsAmountOrSkeleton === null) {
			return 'Docs';
		}

		return <>Docs ({documentsAmountOrSkeleton})</>;
	}

	get title() {
		const { documentsAmountOrSkeleton } = this;

		if (documentsAmountOrSkeleton === null) {
			return 'Documents';
		}

		return <>Documents ({documentsAmountOrSkeleton})</>;
	}

	readonly tab = PageTab.DocumentsList;
	readonly element = <DocumentsListAdapter />;
	readonly path = 'documents';
	readonly icon = <DocumentIcon />;
	readonly featureFlagName = 'clinicalDocuments';
	readonly showSideMenu = true;
	readonly name = 'Document List page';
}

export const PATIENT_PORTRAIT_TABS_DATA = [
	{
		tab: PageTab.DxSummary,
		element: <DxSummaryPage />,
		path: 'dx-summary',
		icon: <BulbIcon />,
		featureFlagName: 'dxInsights',
		showSideMenu: true,
		title: 'Diagnosis Insights',
		shortTitle: 'Dx Insights',
		name: 'Diagnosis Insights page',
	},
	DocumentListTabModel.create({
		getDocumentsAmount: async (patientId: NID) => {
			const response = await getDocumentsByPatientIdOnce({ patientId });
			return response.length;
		},
	}),
	{
		tab: PageTab.ClinicalSummary,
		element: <ClinicalSummaryPage />,
		path: 'clinical-summary',
		icon: <StethoscopeIcon />,
		featureFlagName: 'clinicalSummary',
		showSideMenu: false,
		title: 'Clinical Summary',
		shortTitle: 'Summary',
		name: 'Clinical Summary page',
	},
	{
		tab: PageTab.Quality,
		element: <QualityTabPage />,
		path: 'quality',
		icon: <ShiningDiamondIcon />,
		featureFlagName: 'clinicalAlerts',
		showSideMenu: false,
		title: 'Quality Insights',
		shortTitle: 'Quality',
		name: 'Quality Insights page',
	},
	{
		tab: PageTab.Ambient,
		element: <AmbientTabPage />,
		path: 'ambient',
		icon: <MicrophoneIcon />,
		featureFlagName: 'ambient',
		showSideMenu: true,
		title: 'Ambient',
		shortTitle: 'Ambient',
		name: 'Ambient page',
	},
] as const satisfies ReadonlyArray<Tab>;

export type PortraitPageTab = (typeof PATIENT_PORTRAIT_TABS_DATA)[number];

export const TAB_PATHS = PATIENT_PORTRAIT_TABS_DATA.map(function getTabPath({ path }): typeof path {
	return path;
});
